import React, { FC, useEffect, useState } from 'react'
import { Button, Grid, Paper, Typography } from '@mui/material'
import { DataGridPremium, GridColDef, GridPaginationModel } from '@mui/x-data-grid-premium'
import { ContractorTransfer } from 'src/api'
import { DataLoad, InnerLink, TableToolbar } from 'src/components/ui'
import { ContractorTransferColumnsDataGrid } from 'src/components/columns/contractorTransfer'
import { isTransferFinal, TRANSFER_POLL_INTERVAL_MS } from 'src/components/columns/contractorTransferHelpers'
import { useContractorTransferListRequest } from 'src/hooks/api'
import { useQueryParams } from 'src/hooks/ui'
import { SearchParamsKeys } from 'src/enums'
import { defaultPage, defaultPerPage, defaultRowsValuePerPage } from 'src/config'

const columns: GridColDef<ContractorTransfer>[] = [
  ContractorTransferColumnsDataGrid.Id,
  ContractorTransferColumnsDataGrid.Source,
  ContractorTransferColumnsDataGrid.Target,
  ContractorTransferColumnsDataGrid.Author,
  ContractorTransferColumnsDataGrid.Status,
  ContractorTransferColumnsDataGrid.CreatedAt,
  ContractorTransferColumnsDataGrid.FinishedAt
]

/** The Transfer tab of the Settings page: the entry point of the transfer wizard and the history of all transfers. */
export const ContractorTransferHistory: FC = () => {
  const [params, setQueryParams] = useQueryParams({
    page: `${defaultPage}`,
    rowsPerPage: `${defaultPerPage}`
  })
  // only the paging keys: the Settings page also keeps its tab in the URL
  const listParams: Record<string, string> = { page: params.page, rowsPerPage: params.rowsPerPage, sort: 'createdAt:desc' }
  const { data, isInitialLoading, isError, error } = useContractorTransferListRequest({
    params: listParams,
    options: {
      // keeps the statuses of running transfers fresh, and stops once every row on the page is final
      refetchInterval: (list) => (list?.rows.some((transfer) => !isTransferFinal(transfer.status)) ? TRANSFER_POLL_INTERVAL_MS : false)
    }
  })

  const [rowCountState, setRowCountState] = useState(data?.count || 0)

  useEffect(() => {
    setRowCountState((prev) => (data?.count !== undefined ? data.count : prev))
  }, [data?.count])

  const handleChangePaginationModel = (pagination: GridPaginationModel): void => {
    setQueryParams(
      {
        [SearchParamsKeys.Page]: `${pagination.page + 1}`,
        [SearchParamsKeys.RowsPerPage]: `${pagination.pageSize}`
      },
      [SearchParamsKeys.Page, SearchParamsKeys.RowsPerPage]
    )
  }

  const page = params.page ? parseInt(params.page, 10) - 1 : defaultPage - 1
  const pageSize = params.rowsPerPage ? parseInt(params.rowsPerPage, 10) : defaultPerPage

  return (
    <Grid container direction="column" spacing={2} wrap="nowrap">
      <Grid item>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Move users, projects and devices from one contractor to another. A transfer runs in the background; open a row to follow it.
        </Typography>
        <InnerLink to="/settings/transfers/new" style={{ textDecoration: 'none' }}>
          <Button variant="contained">New transfer</Button>
        </InnerLink>
      </Grid>
      <Grid item minWidth={0}>
        <DataLoad isLoading={false} isError={isError} errorMessage={error?.message}>
          <Paper sx={{ flexGrow: 1, minHeight: '200px', width: '100%' }}>
            <DataGridPremium<ContractorTransfer>
              rows={data?.rows || []}
              columns={columns}
              pagination
              autoHeight
              disableRowSelectionOnClick
              loading={isInitialLoading}
              rowCount={rowCountState}
              paginationModel={{ page, pageSize }}
              paginationMode="server"
              pageSizeOptions={defaultRowsValuePerPage}
              onPaginationModelChange={handleChangePaginationModel}
              slots={{ toolbar: () => <TableToolbar exportedFileName={`${document.title}_transfers`} /> }}
              sx={{
                border: 'none',
                width: '100%',
                '& .MuiDataGrid-columnHeaderTitle': {
                  whiteSpace: 'normal',
                  lineHeight: 1,
                  fontSize: '13px',
                  fontWeight: 400,
                  color: 'rgba(0, 0, 0, 0.6)',
                  overflow: 'visible'
                }
              }}
            />
          </Paper>
        </DataLoad>
      </Grid>
    </Grid>
  )
}
